import { topbar } from '@ui/theme/values'
import { useCallback, useRef } from 'react'
import { useWindowDimensions, View } from 'react-native'
import { scheduleOnUI } from 'react-native-worklets'
import Animated, {
  measure,
  MeasuredDimensions,
  useAnimatedRef,
  useAnimatedScrollHandler,
  useDerivedValue,
  useSharedValue,
} from 'react-native-reanimated'

export const useRecommendedScroll = ({
  offset = 0,
  enabled = true,
}: {
  offset?: number
  enabled?: boolean
}) => {
  const recommendedRef = useAnimatedRef<View>()
  const scrollViewRef = useRef<Animated.ScrollView>(null)
  const { height: windowHeight } = useWindowDimensions()
  const measured = useSharedValue<MeasuredDimensions | null>(null)
  const scrollY = useSharedValue(0)

  const visible = useDerivedValue(() => {
    if (!measured.value) {
      return 0
    }
    const top = measured.value.pageY - windowHeight + offset - scrollY.value
    const visiblePixes = Math.max(0, Math.min(measured.value.height, top * -1))

    const visible = 1 - visiblePixes / measured.value.height
    return visible
  }, [measured])

  const onScrollHandler = useAnimatedScrollHandler({
    onScroll: (event) => {
      if (!measured.value) {
        measured.set(measure(recommendedRef))
      }
      scrollY.set(event.contentOffset.y)
    },
  })

  const onRecommendationPress = useCallback(() => {
    if (!measured.value) {
      return
    }
    scrollViewRef.current?.scrollTo({
      y: scrollY.value + measured.value.pageY - topbar.height - 30,
      animated: true,
    })
  }, [measured, scrollY])

  const onRecommendationLayout = useCallback(() => {
    scheduleOnUI(() => {
      'worklet'
      measured.set(measure(recommendedRef))
    })
  }, [measured, recommendedRef])

  return {
    refs: {
      scroll: scrollViewRef,
      recommended: recommendedRef,
    },
    handlers: {
      // Make sure we don't run handlers if the feature is disabled
      onScroll: enabled ? onScrollHandler : undefined,
      onPress: onRecommendationPress,
      onRecommendationLayout: enabled ? onRecommendationLayout : undefined,
    },
    animated: {
      visible,
    },
  }
}
